import React from 'react'
import { LeaveDisplayFormatEnum, QuotaDisplayFormatEnum } from '../../types/display-in-days-hours'
import { IQuotaBoxProps } from './types'
import { BroughtForwardTooltip, ExpirationTooltip, QuotaTooltip, RemainingDaysTooltip, ToilTooltip, UsedDaysTooltip } from '../user-leave-quotas/quota-tooltips'
import { getRawOrFormattedQuota } from '../../functions/get-days-or-hours'
import { IQuotaAmount, QuotaUnits } from '../../types/leave-policy'
import { LocaleEnum } from '../../types/i18n'
import { useIntl } from 'react-intl'
import { getRawQuotaDaysOrHours } from '../../functions/quotas'

type QuotaContext = 'broughtForward' | 'quota' | 'toil' | 'used' | 'remaining' | 'expired'

const QuotaBox = ({
  title,
  dataTestId,
  value,
  valueUnits,
  footer,
  hasUnlimitedDays,
  quotas,
  doNotShowDisplayInDaysOrHoursTooltip = false,
  hasRequestedDays = false,
  locationName,
  leaveTypeName,
  broughtForwardLimit,
  expirationDate,
  locale,
  scheduledDays,
  lapsedToil,
  displayFormat = QuotaDisplayFormatEnum.formatted,
}: IQuotaBoxProps): React.ReactElement => {
  const context = (title as JSX.Element).props?.id?.split('.')?.pop() as QuotaContext
  let tooltip
  switch (context) {
    case 'broughtForward':
      tooltip = (
        <BroughtForwardTooltip
          value={valueUnits === 'hours' ? value.raw?.hours : value.raw?.days}
          locationName={locationName}
          leaveTypeName={leaveTypeName}
          broughtForwardLimit={broughtForwardLimit}
          units={valueUnits}
        />
      )
      break
    case 'quota':
      tooltip = (
        <QuotaTooltip
          value={getRawQuotaDaysOrHours(valueUnits === 'hours', quotas.earnedWithoutRollover)}
          quotas={quotas}
          hasUnlimitedDays={hasUnlimitedDays}
          hourlyLeaveAccounting={valueUnits === 'hours'}
          leaveTypeName={leaveTypeName || ''}
        />
      )
      break
    case 'toil':
      tooltip = <ToilTooltip quotas={quotas} units={valueUnits} />
      break
    case 'used':
      tooltip = <UsedDaysTooltip used={value} scheduled={scheduledDays} units={valueUnits} leaveTypeName={leaveTypeName} />
      break
    case 'remaining':
      tooltip = (
        <RemainingDaysTooltip
          value={valueUnits === 'hours' ? value.raw?.hours : value.raw?.days}
          units={valueUnits}
          leaveTypeName={leaveTypeName}
          broughtForwardLimit={broughtForwardLimit}
          unlimited={hasUnlimitedDays}
        />
      )
      break
    case 'expired':
      tooltip = <ExpirationTooltip expirationDate={expirationDate} locale={locale as LocaleEnum} lapsedToil={lapsedToil} units={valueUnits} />
      break
    default:
      tooltip = <></>
      break
  }

  return (
    <div className={`quota quota-box ${hasRequestedDays ? 'requested' : ''} ${context}`}>
      <div className="quota-box-title" data-testid={`${dataTestId}-title`}>
        {doNotShowDisplayInDaysOrHoursTooltip ? (
          title
        ) : (
          <>
            {title}
            {tooltip}
          </>
        )}
      </div>
      <div className="quota-box-value" data-testid={`${dataTestId}-value`}>
        {formatValue(value, hasUnlimitedDays, displayFormat, valueUnits)}
      </div>
      <div className="quota-box-footer" data-testid={`${dataTestId}-footer`}>
        {footer}
      </div>
    </div>
  )
}

function formatValue(value: IQuotaAmount, isUnlimited: boolean, displayFormat: QuotaDisplayFormatEnum, valueUnits: QuotaUnits): React.ReactElement {
  if (isUnlimited) {
    return <span>∞</span>
  }
  if (value.raw.days === 0 && value.raw.hours === 0) {
    return <span>0</span>
  }
  const { formatMessage } = useIntl()
  return <>{getRawOrFormattedQuota(value, formatMessage, displayFormat === QuotaDisplayFormatEnum.raw, valueUnits, LeaveDisplayFormatEnum.short)}</>
}

export default QuotaBox
